import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import oldLocale from 'dayjs/locale/en';
import timeZone from 'dayjs/plugin/timezone';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import duration from 'dayjs/plugin/duration';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(isTomorrow);
dayjs.extend(timeZone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isToday);

const newLocale: any = {
    name: 'en-custom',
    oldLocale,
};

dayjs.locale(newLocale, null, true);

dayjs.updateLocale('en-custom', {
    ...newLocale,
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: formatSeconds,
        m: formatMinute,
        mm: formatMinutes,
        h: formatHour,
        hh: formatHours,
        d: formatDay,
        dd: formatDays,
        M: formatMonth,
        MM: formatMonths,
        y: formatYear,
        yy: formatYears,
    },
});

export function dateTimeLocalFormatted(date?: string | dayjs.Dayjs) {
    let dv = dayjs(date).local().format();
    return dayjs(dv).locale('en-custom').fromNow(true);
}

export function dateTimeLocal(date?: string | dayjs.Dayjs) {
    return dayjs(date).local();
}

function formatSeconds(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in a few seconds`;
    } else {
        return `Due a few seconds ago`;
    }
}

function formatMinute(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in a minute`;
    } else {
        return `Due a minute ago`;
    }
}

function formatDay(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        if (num === 2) return 'Due tomorrow';
        if (num === 1) return 'Due today';

        return `Due ${
            dayjs().startOf('hour').add(num, 'hours').isSameOrBefore(dayjs(), 'day')
                ? 'today'
                : 'tomorrow'
        }`;
    } else {
        return `Due ${num} days ago`;
    }
}

function formatDays(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (num < 7) {
        if (isFuture) {
            if (num === 2) return 'Due tomorrow';
            if (num === 1) return 'Due today';
            return `Due ${dayjs()
                .startOf('day')
                .add(num, 'days')
                .subtract(1, 'minute')
                .startOf('day')
                .format('dddd')}`;
        } else {
            return `Due ${num} days ago`;
        }
    }
    return isFuture ? `Due in ${num} days` : `Due ${num} days ago`;
}

function formatHours(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due ${
            dayjs().startOf('hour').add(num, 'hours').isSameOrBefore(dayjs(), 'day')
                ? 'today'
                : 'tomorrow'
        }`;
    } else {
        return `Due ${num} hours ago`;
    }
}
function formatHour(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in 1 hour`;
    } else {
        return `Due 1 hour ago`;
    }
}

function formatMinutes(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in ${num} minutes`;
    } else {
        return `Due ${num} minutes ago`;
    }
}

function formatMonths(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in ${num} months`;
    } else {
        return `Due ${num} months ago`;
    }
}
function formatMonth(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in a month`;
    } else {
        return `Due a month ago`;
    }
}

function formatYears(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in ${num} years`;
    } else {
        return `Due ${num} years ago`;
    }
}
function formatYear(num: number, withoutSuffix: boolean, key: string, isFuture: string) {
    if (isFuture) {
        return `Due in a year`;
    } else {
        return `Due a year ago`;
    }
}

export function prettyDateTime(date?: string | dayjs.Dayjs) {
    return dayjs(date).format(dayjs(date) > dayjs().startOf('day') ? 'h:mm A' : 'MMM D, h:mm A');
}

export function prettyDateOrTime(date?: string | dayjs.Dayjs) {
    return dayjs(date).format(dayjs(date) > dayjs().startOf('day') ? 'h:mm A' : 'MMM D');
}

export { dayjs };
